import clsx from "clsx";
import gsap from "gsap";
import { type ComponentProps, useEffect, useRef } from "react";

import { MEDIA_QUERY } from "@widgets/device-info";

import type { MainAboutSectionBase } from "@shared/content-types/schemas/sections.schema";
import { BackgroundMedia } from "@shared/ui/background-media";
import { ButtonWithIcon } from "@shared/ui/button-with-icon";
import { Container } from "@shared/ui/container";
import { SplitText } from "@shared/ui/split-text";
import { Heading } from "@shared/ui/typography";

import s from "./main-about-section.module.scss";

export type MainAboutSectionProps = ComponentProps<"div"> &
  MainAboutSectionBase & {
    className?: string;
    /** Поле "Заголовок" сингл-тайпа главной страницы — текст для h1 */
    pageTitle?: string | null;
  };

export const MainAboutSection = (props: MainAboutSectionProps) => {
  const { className, subtitle, title, background, buttons, pageTitle } = props;

  /**
   * h1 страницы — левая подпись секции (s.title). Текст берётся из поля
   * "Заголовок" сингл-тайпа главной; подзаголовок блока остаётся запасным
   * вариантом, чтобы при пустом поле страница не осталась без h1.
   */
  const pageHeading = pageTitle?.trim() || subtitle;

  const $header = useRef(null);

  useEffect(() => {
    const mm = gsap.matchMedia();

    const initAnimation = (pos: { start: string; end: string }) => {
      const q = gsap.utils.selector($header.current);
      const words = q(".word");

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: $header.current,
          scrub: 1,
          ...pos,
        },
      });

      words.forEach((word) => {
        tl.to(word, {
          opacity: 1,
          duration: 1,
          ease: "none",
        });
      });

      return () => {
        tl.revert();
      };
    };

    mm.add(
      {
        isDesktop: `${MEDIA_QUERY.DESKTOP}`,
        isTablet: `${MEDIA_QUERY.TABLET_ALT}`,
        reduceMotion: "(prefers-reduced-motion: reduce)",
      },
      (context) => {
        const { isDesktop } = context?.conditions || {};

        const unbind = initAnimation({
          start: isDesktop ? "top center" : "top 90%",
          end: isDesktop ? "bottom 70%" : "bottom 80%",
        });

        return () => {
          unbind();
        };
      },
    );
  }, []);

  return (
    <section className={clsx(s.root, className)}>
      {background && (
        <BackgroundMedia
          {...background}
          wrapperClassName={s.bgWrapper}
          className={s.bg}
        />
      )}
      <Container className={s.inner}>
        {pageHeading != null && pageHeading !== "" && (
          <div className={s.side}>
            <h1 className={s.title}>{pageHeading}</h1>
          </div>
        )}
        <div className={s.content}>
          {title != null && title !== "" && (
            <Heading ref={$header} level={3} tag="h2" className={s.header}>
              <SplitText type="word">{title}</SplitText>
            </Heading>
          )}
          {buttons && buttons?.length > 0 && (
            <div className={s.btns}>
              {buttons.map((button, index) =>
                button ? (
                  <ButtonWithIcon
                    key={(button as { id?: number }).id ?? `button-${index}`}
                    href={button?.link}
                    iconName={button?.pageIcon?.icon?.name ?? "plus"}
                    iconFillColor={button?.pageIcon?.fillColor ?? "azure"}
                    iconStrokeColor={button?.pageIcon?.strokeColor ?? "white"}
                    iconSvgCode={button?.pageIcon?.uicon?.svg_code}
                    color="azure"
                    className={s.button}
                    variant={index === 0 ? "secondary-light" : undefined}
                  >
                    {button?.text ?? ""}
                  </ButtonWithIcon>
                ) : null,
              )}
            </div>
          )}
        </div>
      </Container>
    </section>
  );
};

MainAboutSection.displayName = "MainAboutSection";
